import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractDialog, AbstractDialogResultData, DIALOG_RETURN_TYPES, DialogComponent, DialogService } from '@next/core-lib/dialog';
import { AgrI18nSharedModule, LocaleService } from '@next/core-lib/i18n';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';

import { DateService } from '../../../shared/services/date.service';
import { ObservationCropFieldInfo, ObservationHistoryComponent } from '../observation-history/observation-history.component';
import {
  DownyMildewObservationDialogData,
  DownyMildewObservationRegistrationRequest,
  DownyMildewObservationRemovalRequest,
  DownyMildewObservationSeverity,
} from '../../models/observation.models';
import { DownyMildewAIIdentifiers } from '../../shared/constants';
import { DownyMildewSignalStore } from '../../signal-store/downy-mildew-signal-store';
import { map, startWith, take } from 'rxjs';
import { ImageDialogComponent } from 'src/app/shared/components/image-dialog/image-dialog.component';
import { ImmediateErrorStateMatcher } from 'src/app/shared/utils';

@Component({
  selector: 'app-observation-dialog',
  templateUrl: './observation-dialog.component.html',
  styleUrls: ['./observation-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatTooltipModule,
    DialogComponent,
    AgrI18nSharedModule,
    ObservationHistoryComponent,
  ],
})
export class ObservationDialogComponent extends AbstractDialog<DownyMildewObservationDialogData> {
  data = inject<DownyMildewObservationDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<MatDialogRef<ObservationDialogComponent, AbstractDialogResultData<DownyMildewObservationDialogData>>>(MatDialogRef);

  readonly downyMildewStore = inject(DownyMildewSignalStore);
  private readonly dateService = inject(DateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogService = inject(DialogService);
  private readonly matDialog = inject(MatDialog);
  private readonly localeService = inject(LocaleService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  readonly maxDate = this.dateService.now();
  readonly dateErrorStateMatcher = new ImmediateErrorStateMatcher();
  private readonly observationExampleImagePathBySeverity: Record<DownyMildewObservationSeverity, string> = {
    [DownyMildewObservationSeverity.SingleShoot]: 'assets/images/bbch/single-shoot.jpg',
    [DownyMildewObservationSeverity.Spot]: 'assets/images/bbch/haard.jpg',
  };

  private readonly observationExampleImageTitleBySeverity: Record<DownyMildewObservationSeverity, string> = {
    [DownyMildewObservationSeverity.SingleShoot]: 'downyMildew.observation.exampleImageTitleSingleShoot',
    [DownyMildewObservationSeverity.Spot]: 'downyMildew.observation.exampleImageTitleSpot',
  };
  readonly isReadonlyMode = Boolean(this.data.readOnly);

  protected readonly severityLevels = [
    DownyMildewObservationSeverity.SingleShoot,
    DownyMildewObservationSeverity.Spot,
  ];

  readonly selectedCropFieldIds = signal<number[]>(this.data.cropFields.map((field) => field.cropFieldId));
  private readonly hasSubmitted = signal(false);

  form = new FormGroup({
    date: new FormControl<Date>(this.data.actionDate, { nonNullable: true, validators: [Validators.required] }),
    severity: new FormControl<DownyMildewObservationSeverity | null>(null, [Validators.required]),
  });

  readonly isFormValid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map(() => this.form.valid),
    ),
    { initialValue: this.form.valid },
  );

  readonly registrationSucceeded = computed(() => {
    return (
      this.hasSubmitted() &&
      !this.downyMildewStore.observationRegistrationIsPending() &&
      !this.downyMildewStore.observationRegistrationError() &&
      this.downyMildewStore.observationRegistrationStatus() === 'success'
    );
  });

  readonly disableConfirmButton = computed(() => this.isSubmitDisabled);

  constructor() {
    super();

    if (this.isReadonlyMode) {
      this.form.disable({ emitEvent: false });
    }

    this.data.confirmButton = false;
    this.data.disableConfirmButton = this.disableConfirmButton();

    this.updateObservationSelection();

    effect(() => {
      this.data.disableConfirmButton = this.disableConfirmButton();
      this.cdr.markForCheck();
    });

    effect(() => {
      if (this.registrationSucceeded()) {
        this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm });
      }
    });
  }

  get isSubmitDisabled(): boolean {
    return (
      this.isReadonlyMode ||
      !this.isFormValid() ||
      this.downyMildewStore.observationRegistrationIsPending() ||
      this.selectedCropFieldIds().length === 0
    );
  }

  get isSubmitting(): boolean {
    return this.downyMildewStore.observationRegistrationIsPending();
  }

  getCropFieldName(cropFieldId: number): string {
    const cropField = this.data.cropFields.find((field) => field.cropFieldId === cropFieldId);
    return cropField?.cropFieldName ?? '';
  }

  getCropFieldArea(cropFieldId: number): number {
    const cropField = this.data.cropFields.find((field) => field.cropFieldId === cropFieldId);
    return cropField?.areaInHectares ?? 0;
  }

  get selectedCropFieldInfos(): ObservationCropFieldInfo[] {
    return this.selectedCropFieldIds().map((cropFieldId) => ({
      id: cropFieldId,
      name: this.getCropFieldName(cropFieldId),
    }));
  }

  removeCropField(cropFieldId: number): void {
    if (this.isReadonlyMode) {
      return;
    }

    this.selectedCropFieldIds.update((ids) => ids.filter((id) => id !== cropFieldId));
    this.updateObservationSelection();
  }

  onRemoveObservation(request: DownyMildewObservationRemovalRequest): void {
    if (this.isReadonlyMode) {
      return;
    }

    this.dialogService
      .confirm({
        title: this.localeService.translate('downyMildew.observationHistory.deleteConfirmMessage'),
        pushHistoryState: false,
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.downyMildewStore.removeObservation(request);
        }
      });
  }

  onObservationInfoClicked(severity: DownyMildewObservationSeverity): void {
    this.matDialog.open(ImageDialogComponent, {
      data: {
        imagePath: this.observationExampleImagePathBySeverity[severity],
        titleTranslationKey: this.observationExampleImageTitleBySeverity[severity],
        imageAltTranslationKey: this.observationExampleImageTitleBySeverity[severity],
        imageMissingTranslationKey: 'downyMildew.observation.exampleImageMissing',
      },
      maxWidth: '90vw',
      maxHeight: '90vh',
    });
  }

  submitObservation(): void {
    if (this.isSubmitDisabled) {
      return;
    }

    const date = this.form.controls.date.value;
    const severity = this.form.controls.severity.value;

    if (!date || !severity) {
      return;
    }

    const request: DownyMildewObservationRegistrationRequest = {
      cropFieldIds: this.selectedCropFieldIds(),
      observationDate: this.formatDate(date),
      severity,
    };

    this.ai.trackEvent(DownyMildewAIIdentifiers.submitObservation, {});
    this.hasSubmitted.set(true);
    this.downyMildewStore.registerObservation(request);
  }

  private formatDate(date: Date): string {
    // Coerce to native Date to support MomentDateAdapter returning a Moment object from the datepicker
    const nativeDate = new Date(+date);
    const day = nativeDate.getDate().toString().padStart(2, '0');
    const month = (nativeDate.getMonth() + 1).toString().padStart(2, '0');
    const year = nativeDate.getFullYear();
    return `${year}-${month}-${day}`;
  }

  private updateObservationSelection(): void {
    this.downyMildewStore.setObservationSelection(this.selectedCropFieldIds(), this.data.cropYear);
  }
}
